<script setup lang="ts">
import { ChipComponent, DialogModal } from '@metapic-apps/shared'
import { computed, ref, watch } from 'vue'

import { useCreateAdvertiserReport, useGetAdvertiserListRequest } from '@/api'
import { ADVERTISER_REPORT_PAGE_TEMPLATES } from '@/lib/constants'
import type { AdvertiserListItemType } from '@/lib/types/advertiser.types'
import { BoxComponent, ButtonComponent, FlexComponent, InputComponent, SelectComponent, TypographyComponent } from '@/ui-library'

type LocaleOption = { label: string; value: string }

// BCP 47 locale tags, e.g. 'en-GB'
const REPORT_LOCALE_OPTIONS: LocaleOption[] = [
  { label: 'Swedish (sv-SE)', value: 'sv-SE' },
  { label: 'English (en-GB)', value: 'en-GB' },
  { label: 'German (de-DE)', value: 'de-DE' },
  { label: 'Danish (da-DK)', value: 'da-DK' },
  { label: 'Norwegian (no-NO)', value: 'no-NO' },
  { label: 'Finnish (fi-FI)', value: 'fi-FI' },
  { label: 'Dutch (nl-NL)', value: 'nl-NL' },
  { label: 'French (fr-FR)', value: 'fr-FR' },
  { label: 'Spanish (es-ES)', value: 'es-ES' },
  { label: 'Italian (it-IT)', value: 'it-IT' },
  { label: 'Polish (pl-PL)', value: 'pl-PL' },
]

const DEFAULT_LOCALE = 'en-GB'

// Seed the report with one default page so the create request satisfies the
// API's `pages >= 1` requirement; the admin builds out the rest in the editor.
const SEED_PAGE_INPUT = ADVERTISER_REPORT_PAGE_TEMPLATES.find((template) => template.label === 'Cover slide')?.input ?? ''

const props = defineProps<{
  storeId: number
  defaultLanguage?: string
}>()

// The advertiser's own language is a base code (e.g. 'de'); match it to a
// supported report locale by its language subtag, falling back to en-GB.
const matchedLocale = REPORT_LOCALE_OPTIONS.find(
  (option) => option.value === props.defaultLanguage || option.value.split('-')[0] === props.defaultLanguage,
)

const emits = defineEmits<{
  created: [uuid: string]
}>()

const isOpen = defineModel<boolean>('modelValue', { default: false })

const name = ref('')
const locale = ref(matchedLocale?.value ?? DEFAULT_LOCALE)

// Optional default reporting window. The daterange picker inherently keeps
// start <= end and is both-or-neither (a range or nothing), matching the API's
// validation. Picked calendar days map to a full UTC day: start of the first
// day to the end of the last.
const defaultPeriod = ref<[string, string] | null>(null)

// Advertiser search — initialize query with storeId so makeQueryString adds
// or_id which makes the API return the current advertiser on first fetch
const advertisersQuery = ref(String(props.storeId))
const advertiserGroupId = computed(() => undefined as number | undefined)

const { data: advertiserOptions, isLoading: isAdvertisersLoading } = useGetAdvertiserListRequest(advertiserGroupId, advertisersQuery)

const selectedAdvertisers = ref<AdvertiserListItemType[]>([])

// Pre-select the current advertiser once the initial fetch resolves, then
// reset the query so the dropdown shows all advertisers on subsequent opens
watch(
  advertiserOptions,
  (options) => {
    if (options.length > 0 && selectedAdvertisers.value.length === 0) {
      const current = options.find((o) => o.id === props.storeId)
      if (current) {
        selectedAdvertisers.value = [current]
        advertisersQuery.value = ''
      }
    }
  },
  { once: true },
)

const onAdvertiserSearch = (str: string) => (advertisersQuery.value = str)

const onRemoveAdvertiser = (item: AdvertiserListItemType) => {
  if (item.id === props.storeId) {
    return
  }
  selectedAdvertisers.value = selectedAdvertisers.value.filter((a) => a.id !== item.id)
}

const { createReport, isLoading } = useCreateAdvertiserReport()

const isSubmitDisabled = computed(() => !name.value.trim() || !locale.value || selectedAdvertisers.value.length === 0)

const onSubmit = async () => {
  if (isSubmitDisabled.value) {
    return
  }
  const uuid = await createReport({
    name: name.value.trim(),
    locale: locale.value,
    store_ids: selectedAdvertisers.value.map((a) => a.id),
    pages: [{ input: SEED_PAGE_INPUT }],
    ...(defaultPeriod.value
      ? {
          default_period_start: `${defaultPeriod.value[0]}T00:00:00Z`,
          default_period_end: `${defaultPeriod.value[1]}T23:59:59Z`,
        }
      : {}),
  })
  if (uuid) {
    emits('created', uuid)
  }
}
</script>

<template>
  <DialogModal v-model="isOpen" width="640" @before-close="() => (isOpen = false)">
    <template #header>
      <TypographyComponent variant="headline-medium">Create advertiser report</TypographyComponent>
    </template>

    <template #default>
      <BoxComponent :mt="16" :mb="24">
        <FlexComponent direction="column" :gap="16">
          <InputComponent v-model="name" placeholder="e.g. OTTO Q1 2026 Campaign Report">
            <template #header>
              <TypographyComponent variant="title-medium">Report name</TypographyComponent>
            </template>
          </InputComponent>

          <div>
            <SelectComponent
              v-model="selectedAdvertisers"
              :is-loading="isAdvertisersLoading"
              :options="advertiserOptions"
              :remote-method="onAdvertiserSearch"
              title="Advertisers"
              placeholder="Search advertisers"
              value-key="id"
              multiple
              remote
              filterable
              :teleported="false"
              :extra-class="selectedAdvertisers.length ? 'report-advertiser-selector--wrapper' : ''"
            >
              <template #label>
                <div />
              </template>
            </SelectComponent>
            <FlexComponent v-if="selectedAdvertisers.length > 0" wrap="wrap" :gap="8" class="report-advertiser-selector--tags">
              <ChipComponent
                v-for="item in selectedAdvertisers"
                :key="item.id"
                :closable="item.id !== storeId"
                @close="onRemoveAdvertiser(item)"
              >
                {{ item.label }}
              </ChipComponent>
            </FlexComponent>
          </div>

          <SelectComponent v-model="locale" title="Language" :options="REPORT_LOCALE_OPTIONS" :teleported="false" />

          <FlexComponent direction="column" :gap="8">
            <TypographyComponent variant="title-medium">Default reporting period</TypographyComponent>
            <TypographyComponent variant="body-small" color="var(--secondary-48)">
              Optional. Sets the default date window every page uses unless its prompt says otherwise.
            </TypographyComponent>
            <el-date-picker
              v-model="defaultPeriod"
              type="daterange"
              value-format="YYYY-MM-DD"
              range-separator="to"
              start-placeholder="Start date"
              end-placeholder="End date"
              :teleported="false"
              :clearable="true"
              class="report-period-picker"
            />
          </FlexComponent>
        </FlexComponent>
      </BoxComponent>

      <FlexComponent :gap="8" justify="flex-end">
        <ButtonComponent variant="text" :is-disabled="isLoading" @click="() => (isOpen = false)">Cancel</ButtonComponent>
        <ButtonComponent variant="primary" :is-loading="isLoading" :is-disabled="isSubmitDisabled" @click="onSubmit">
          Create report
        </ButtonComponent>
      </FlexComponent>
    </template>
  </DialogModal>
</template>

<style lang="scss" scoped>
:deep(.report-advertiser-selector--wrapper) {
  .el-select__wrapper {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;

    .el-select__selection.is-near {
      padding: 7px 0;
      margin-left: 0;
    }
  }
}

:deep(.report-advertiser-selector--wrapper) {
  .el-select__selected-item:not(.el-select__input-wrapper):not(.is-transparent) {
    display: none;
  }
}

.report-advertiser-selector--tags {
  padding: 8px;
  border-radius: 0 0 8px 8px;
  background: var(--inverted);
  margin-top: -1px;
  box-shadow: 0 0 0 1px var(--secondary-12) inset;
}

.report-period-picker {
  width: 100%;
}
</style>
